import { takeEvery, call, select, all, put, fork, spawn, takeLatest } from 'redux-saga/effects';
import { requestApi } from '../../request-api';
import { store } from '../../store.js';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils';
import * as auth from '../auth';
import * as device from '../device';
import * as router from '../router';
import * as actions from './actions.js';
import * as selectors from './selectors.js';
import get from 'lodash-es/get';
import unset from 'lodash-es/unset';
import isEmpty from 'lodash-es/isEmpty';
import firestoreRedux from '@dreamworld/firestore-redux';
import * as boardTranslations from '../board-translations';
import * as multipleLanguage from '../multiple-language';
import * as user from '../user';
import URI from '@dw/urijs-esm';
import { detectAdBlocker } from '../../utils/detect-adblock.js';

//Analytics
import * as cookieConsent from '@kerika/cookie-consent';
import { isAnalyticsAllowed } from '../../analytics/utils.js';
import * as analytics from '../../analytics/analytics.js';
import * as posthog from '../../analytics/posthog.js';
import * as gtm from '../../analytics/gtm.js';
import * as linkedIn from '../../analytics/linkedin.js';

let previousUseCaseBoards;
let unsubscribeLoadUseCaseBoards;

// Track analytics initialization status
let analyticsInitialized = false;
let postHogInitialized = false;

function* loadUseCaseBoards() {
  try {
    const state = yield select();
    const defaultLanguage = multipleLanguage.selectors.defaultLanguage(state) || 'en';
    const currentLanguage = multipleLanguage.selectors.currentLanguage(state);
    const langs = defaultLanguage === currentLanguage ? [defaultLanguage] : [defaultLanguage, currentLanguage];
    firestoreRedux.query('use-case-boards', { where: [['language', 'in', langs]], id: 'use-case-boards' });
    unsubscribeLoadUseCaseBoards && unsubscribeLoadUseCaseBoards();
    unsubscribeLoadUseCaseBoards = ReduxUtils.subscribe(store, `firestore.docs.use-case-boards`, async (boards) => {
      const promises = [];
      const userIndustry = user.selectors.jobRole(store.getState()) || 'something-else';
      const userIndustryBoards = boards ? Object.values(boards).filter(board => board && board.industries && board.industries.includes(userIndustry)): [];
      const industryBoard = userIndustryBoards && userIndustryBoards[0] && userIndustryBoards[0].boardId || '';
      if(industryBoard) {
        try {
          const query = firestoreRedux.getDocById('boards', industryBoard, { requesterId: 'use-case-boards' });
          await query.result;
        } catch (error) {}
      }
      const newBoardsIds = boards ? Object.values(boards).map(board => board?.boardId) : [];
      const previousBoardsIds = previousUseCaseBoards ? Object.values(previousUseCaseBoards).map(board => board?.boardId) : [];
      const addedBoards = newBoardsIds.filter(boardId => !previousBoardsIds.includes(boardId)) || [];
      for (let i = 0; i < addedBoards.length; i++) {
        const id = addedBoards[i];
        if(id) {
          const boardsQuery = firestoreRedux.getDocById('boards', id, {requesterId: 'use-case-boards' });
          promises.push(boardsQuery.waitTillResultAvailableInState());
        }
      }
      previousUseCaseBoards = boards;
      try {
        if(!isEmpty(promises)) {
          await Promise.all(promises);
        }
      } catch (error) {
        console.error('Error loading use case boards:', error);
      }
    });
  } catch (error) {
    console.error('Error loading use case boards:', error);
  }
}

function* loadAppData() {
  //Load system language
  firestoreRedux.getDocById(`system-language`, `default`);

  //Load system language
  firestoreRedux.getDocById(`country-wise-lang`, `default`);

  //Load beta users
  firestoreRedux.getDocById('beta-users', 'bus_0');

  firestoreRedux.query('app', { requesterId: 'app-data' });

  yield spawn(loadBoardSettingsData);
  yield spawn(loadJobRoleDetails);

  //Load malicious-urls
  try {
    const maliciousUrlsQuery = firestoreRedux.getDocById(`malicious-urls`, 'default');
    yield maliciousUrlsQuery.result;
    yield spawn(maliciousUrlRedirect);
  } catch (error) {
    console.error('maliciousUrls > loads failed due to this: ', error);
  }

  // PostHog first and on its own: it initialises whether or not consent has been given, so the
  // SDK is present on the landing page to record the campaign that brought the visitor in.
  yield spawn(initializePostHog);

  // Initialize analytics after config is loaded (config wait is inside initializeAnalytics)
  yield spawn(initializeAnalytics);

  yield spawn(detectAdBlockerSaga);
}

/**
 * Runs the ad blocker detection probe once per session and records the result.
 * Spawned during app init; not triggered by any action.
 */
function* detectAdBlockerSaga() {
  try {
    const { detected, source } = yield call(detectAdBlocker);
    yield put(actions.setAdBlockerDetected(detected, source));
  } catch (error) {
    console.error('app > detectAdBlockerSaga > failed due to this: ', error);
  }
}

/**
 * Load job-role details.
 */
function* loadJobRoleDetails() {
  try {
    firestoreRedux.getDocById('user-property-definitions', 'job-role');
  } catch (error) {
    console.error("app > loadJobRoleDetails > failed due to this: ", error);
  }
}

/**
 * Load default board settings.
 */
let lastTaskBoardId;
function* loadBoardSettingsData() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const query = firestoreRedux.getDocById('board-settings', 'bs_0');
  const response = yield query.result;
  const taskBoardId = get(response, 'defaultTemplates.TASKBOARD') || get(response, 'defaultTemplates.en.TASKBOARD');
  
  if(!userId) {
    return;
  }

  if(lastTaskBoardId === taskBoardId) {
    return;
  }

  if(taskBoardId) {
    yield put(boardTranslations.actions.load(`app-data`, taskBoardId));
    lastTaskBoardId = taskBoardId;
  }
}

let previousEventId;
function* routeChangeHandler() {
  yield spawn(redirectOnNewDomainApp);
  yield spawn(maliciousUrlRedirect);
  const state = yield select();
  const currentEventId = router.selectors.analyticsEventId(state);
  if (previousEventId !== currentEventId && currentEventId) {
      yield spawn(trackAnalyticsEvent, currentEventId);
  }
  previousEventId = currentEventId;
}

function* maliciousUrlRedirect() {
  const state = yield select();
  const pathname = window.location.pathname;
  const query = window.location.search;
  const url = `${pathname}${query}`;
  if(selectors.isMaliciousUrl(state, url)) {
    const redirectUrl = selectors.getMaliciousRedirectUrl(state, url);
    if(redirectUrl) {
      window.location.href = redirectUrl;
    }
  }
}

/**
 * Wait till firestore config is not available.
 */
let firestoreConfigAlreadyAvailable = false;
function waitTillFirestoreConfigAvailable() {
  if (firestoreConfigAlreadyAvailable) {
    return Promise.resolve();
  }
  
  let resolve, reject;
  const promise = new Promise((res, rej) => { resolve = res, reject = rej; });
  const unsubscribe = ReduxUtils.subscribe(store, `firestore.docs.app.config`, (config) => {
    if (config !== undefined) {
      unsubscribe && unsubscribe();
      firestoreConfigAlreadyAvailable = true;
      resolve(config);
    }
  });
  return promise;
}

function* redirectOnNewDomainApp() {
  try {
    console.debug('redirectOnNewDomainApp: Start');
    let state = yield select();
    let config = selectors.config(state);

    const isInstalledApp = selectors.isInstalledApp(state);
    if (isInstalledApp) {
      console.debug('redirectOnNewDomainApp: Skipping domain redirect for installed app');
      return;
    }

    const currentHost = window.location.host;
    if (!currentHost.startsWith('a.')) {
      console.debug('redirectOnNewDomainApp: Current host does not start with "a.", skipping redirect');
      return;
    }

    console.debug('redirectOnNewDomainApp: Waiting for Firestore config to be available');
    yield call(waitTillFirestoreConfigAvailable);
    state = yield select();
    config = selectors.config(state);

    const redirectEnabled = get(config, 'redirectOnNewDomainApp', false);
    console.debug('redirectOnNewDomainApp: Redirect enabled:', redirectEnabled);
    if (!redirectEnabled) {
      console.debug('redirectOnNewDomainApp: Redirect is not enabled, skipping');
      return;
    }

    const baseUrl = get(config, 'baseUrl', '');
    if (!baseUrl) {
      console.error('redirectOnNewDomainApp: Base URL is not defined in config, skipping redirect');
      return;
    }

    const url = new URL(window.location.href);
    const newHost = baseUrl.replace(/^https?:\/\//, '');
    if (newHost === currentHost) {
      console.debug('redirectOnNewDomainApp: Current host matches the new host, no redirect needed');
      return;
    }

    url.host = newHost;
    console.debug('redirectOnNewDomainApp: Redirecting to new host:', newHost);
    window.location.href = url.toString();
  } catch (error) {
    console.error('Error in redirectOnNewDomainApp:', error);
  }
}

/**
 * @param {Number} eventId event id.
 * Called server API to gets a analytics-events details from eventId.
 * Track analytics event.
 */
function* trackAnalyticsEvent(eventId) {
  const state = yield select();
  const currentUserId = auth.selectors.currentUserId(state) || null;
  try {
    yield call(removeAnalyticsEventId);
    const eventDetails = yield requestApi(`/import-board/analytics-events/${eventId}`, {method: 'GET'});
    if(!isEmpty(eventDetails)) {
      const eventName = get(eventDetails, 'type');
      const detail = get(eventDetails, 'detail', {});
      const userId = get(detail, 'user_id', currentUserId);
      const memberId = currentUserId || get(detail, 'member_id');
      if(eventName && userId) {
        console.debug("Analytics-events > track. details: ", JSON.stringify(eventDetails));
        unset(detail, 'user_id');
        analytics.setUserProperties(userId, {}, true);
        analytics.logEvent(eventName, detail);
        analytics.setUserProperties(memberId, {}, true);
      } else {
        console.error("Analytics-events > details are not proper.  details: ", JSON.stringify(eventDetails));
      }
    }
  } catch (error) {
    yield call(removeAnalyticsEventId);
  }
}

/**
 * Remove analytics event id and replace url.
 */
function* removeAnalyticsEventId() {
  router.actions.setQueryParams({evt: null});
}

/**
 * Initialize PostHog.
 *
 * Deliberately not behind the consent check that guards `initializeAnalytics`. PostHog starts on
 * the landing page either way; until consent it holds everything in memory with capturing opted
 * out, so nothing is stored on the device and nothing is sent. That is what lets it see the ad
 * click id in the landing URL — by the time a visitor answers the cookie banner, that URL is gone.
 */
function* initializePostHog() {
  if (postHogInitialized) {
    return;
  }

  yield call(waitTillFirestoreConfigAvailable);

  const state = yield select();
  const config = selectors.config(state);

  if (auth.selectors.impersonatedUser(state)) {
    console.log('Skipping PostHog initialization for impersonated user');
    return;
  }

  try {
    const posthogConfig = get(config, 'analytics.posthog') || {};
    const posthogAPIKey = posthogConfig.apiKey;
    const posthogAPIHost = posthogConfig.apiHost;
    const posthogEnabled = posthogConfig.enabled === true || posthogConfig.enabled === 'true' || false; // default to false if not specified

    if (!posthogAPIKey || !posthogEnabled) {
      console.log('PostHog initialization skipped: disabled in config');
      return;
    }

    // Use Sentry configuration from PostHog config for PostHog integration
    const sentryConfig = {
      organization: posthogConfig.organization,
      projectId: posthogConfig.projectId
    };

    posthog.init({
      APIKey: posthogAPIKey,
      APIHost: posthogAPIHost,
      userId: auth.selectors.currentUserId(state),
      sentryConfig
    }).catch(error => console.warn('Failed to load PostHog:', error));
    postHogInitialized = true;
    console.log('PostHog initialized');
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
  }
}

/**
 * Initialize all analytics services.
 * Called on app startup and when cookie consent is updated.
 */
function* initializeAnalytics() {
  // Check if analytics already initialized
  if (analyticsInitialized) {
    console.log('Analytics already initialized - skipping reinitialization');
    return;
  }

  // Check if analytics is allowed based on cookie consent
  if (!isAnalyticsAllowed()) {
    console.log('Analytics initialization skipped: Analytics consent not given');
    return;
  }

  // Wait till firestore config is available
  yield call(waitTillFirestoreConfigAvailable);

  const state = yield select();
  const config = selectors.config(state);
  const impersonatedUser = auth.selectors.impersonatedUser(state);
  const currentUserId = auth.selectors.currentUserId(state);

  if (impersonatedUser) {
    console.log('Skipping analytics initialization for impersonated user');
    return;
  }

  // Initialize GTM
  try {
    const GTMApiKey = config.GTMApiKey;
    const GTMExtraParams = config.GTMExtraParams || '';
    if (GTMApiKey) {
      gtm.init(GTMApiKey, GTMExtraParams);
      console.log('GTM initialized');
    }
  } catch (error) {
    console.error('Failed to initialize GTM:', error);
  }

  // Initialize LinkedIn
  try {
    const linkedInPartnerId = config.linkedInPartnerId;
    if (linkedInPartnerId) {
      linkedIn.init(linkedInPartnerId);
      console.log('LinkedIn initialized');
    }
  } catch (error) {
    console.error('Failed to initialize LinkedIn:', error);
  }

  // Attach UTM as user properties across all analytics services.
  // Runs decoupled from analytics init so a slow user-properties write
  // can't delay the rest of the flow.
  yield spawn(attachUtmUserProperties);

  // Mark analytics as initialized
  analyticsInitialized = true;
  console.log('All analytics services initialization completed');
}

/**
 * Attach UTM details from device-ui as user properties on GTM and PostHog.
 * Sends both the latest UTM and the first-touch (initial) UTM. The initial UTM
 * is sent with set-once semantics so it never overwrites a prior first-touch
 * value on the analytics platform.
 */
function* attachUtmUserProperties() {
  try {
    const state = yield select();
    const currentUserId = auth.selectors.currentUserId(state);
    const deviceId = device.selectors.getId(state);
    const utmDetails = get(state, `firestore.docs.device-ui.du_${deviceId}.utmDetails`) || {};
    const initialUtmDetails = get(state, `firestore.docs.device-ui.du_${deviceId}.initialUtmDetails`) || {};
    if (!currentUserId || (isEmpty(utmDetails) && isEmpty(initialUtmDetails))) {
      return;
    }
    analytics.setUserUtmProperties(currentUserId, {
      latest: !isEmpty(utmDetails) ? utmDetails : undefined,
      initial: !isEmpty(initialUtmDetails) ? initialUtmDetails : undefined
    });
  } catch (error) {
    console.error('Failed to attach UTM user properties:', error);
  }
}

/**
 * Handle cookie consent preferences update.
 * Reinitialize analytics services when preferences change.
 * Note: Each analytics service checks isAnalyticsAllowed() internally.
 */
function* handleCookieConsentUpdate() {
  console.log('Cookie consent updated - checking analytics status');

  // If analytics is disabled, reset the initialization flag
  // This allows analytics to initialize when user enables it later
  if (!isAnalyticsAllowed()) {
    analyticsInitialized = false;
    // Stop sending and clear PostHog off the device; it stays loaded, in memory, opted out.
    posthog.revokeConsent();
    console.log('Analytics disabled - reset initialization flag');
    return;
  }

  // PostHog is already running from the landing page — hand it the consent rather than starting
  // it, so the campaign parameters it captured back then carry over to the persisted store.
  yield call(initializePostHog);
  posthog.grantConsent();

  // Attempt to initialize analytics (will skip if already initialized)
  yield call(initializeAnalytics);

  // Signup only sets the email when consent is already in place, so a visitor who accepts the
  // banner moments later never gets one. Set it here too.
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const email = get(auth.selectors.currentUser(state), 'email');
  if(userId && email) {
    analytics.setIdentifyingUserProperties(userId, { email });
  }
}

/**
 * Watch router change.
 * Dispatch LOGIN_AND_SIGNUP_PAGE_OPENED, LOGIN_AND_SIGNUP_PAGE_CLOSED actions based on previous & current page.
 */
 function* watchRouter() {
  //If page is already opened, check once.
  yield call(routeChangeHandler);
  yield takeEvery(router.actions.UPDATE_ROUTER, routeChangeHandler);
}

/**
 * Show the cookie consent banner if the user is authenticated, not on the
 * login/signup page, and hasn't already resolved consent (e.g. on the Website).
 *
 * Fires on UPDATE_AUTH (covers page refresh — Firebase restores auth silently
 * without dispatching LOGIN_SUCCESS), on UPDATE_ROUTER (covers landing on a
 * post-auth page after the router resolves), and on LOGIN_AND_SIGNUP_PAGE_CLOSED
 * (covers fresh login/signup completing — banner must not appear during the
 * auth flow itself).
 */
function* showCookieConsentBannerIfNeeded() {
  if (cookieConsent.isConsentTaken()) {
    return;
  }
  const state = yield select();
  if (!auth.selectors.currentUserId(state)) {
    return;
  }
  const pageName = router.selectors.pageName(state);
  if (pageName === 'LOGIN_AND_SIGNUP' || pageName === 'LOADING' || pageName === 'SIGNUP') {
    return;
  }
  cookieConsent.showBanner();
}

/**
 * Init Saga.
 */
function* appSaga() {
  yield all([
    fork(watchRouter),
    takeEvery(actions.LOAD_APP_DATA, loadAppData),
    takeEvery(auth.actions.UPDATE_AUTH, loadBoardSettingsData),
    takeLatest(actions.LOAD_USE_CASE_BOARDS, loadUseCaseBoards),
    takeEvery(cookieConsent.redux.actions.UPDATE_PREFERENCES_SUCCESS, handleCookieConsentUpdate),
    takeEvery([auth.actions.UPDATE_AUTH, auth.actions.LOGIN_AND_SIGNUP_PAGE_CLOSED, router.actions.UPDATE_ROUTER], showCookieConsentBannerIfNeeded)
  ]);
}

export default appSaga;
